// Sign-out confirmation: one question, asked once in the title. Chat history and
// workflows are always wiped from this browser before WorkOS revokes
// the session, which addresses Cobalt finding PT38031_6 (residual
// sensitive data in browser storage after sign-out) without asking.
//
// Built directly on OverlayDialog rather than ConfirmDialog so the
// buttons can hold while the wipes run.
//
// Auth tokens are always cleared inside authBridge.logout().
//
// Portaled to document.body: the trigger lives in SidebarFooter, whose
// <aside> ancestor declares `contain: content`. CSS containment makes
// that ancestor a containing block for `position: fixed` descendants,
// so an un-portaled OverlayDialog would anchor to the 300px sidebar
// and overflow it. The portal lifts the dialog out so `fixed inset-0`
// resolves against the viewport.

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import OverlayDialog from "./OverlayDialog.jsx";
import Button from "./ui/Button.jsx";
import { logout } from "../lib/authBridge.js";
import { suppressFurtherWrites as suppressChatWrites } from "../lib/chatStorage.js";
import { clearChatData, clearWorkflowData } from "../lib/clearUserData.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

export default function LogoutDialog({ open, onClose }) {
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setBusy(false);
    }, [open]);

    const onSignOut = async () => {
        if (busy) return;
        setBusy(true);
        // Kill switch for the WorkOS-redirect window: between the
        // awaited wipes and logout()'s nav, ChatConsole's
        // window.beforeunload handler would flush in-memory chat state
        // back to storage. Page is about to navigate away, nothing
        // legitimate left to persist.
        suppressChatWrites();
        // No tenantId passed: clearUserData resolves from /v1/me's cached
        // result. authBridge's getTenantId() reads a localStorage key that
        // the current dashboard never writes, so it always returns null
        // and would silently retarget the wipes at "default".
        // allSettled: a single failing wipe must not block sign-out.
        // Auth tokens still get cleared inside logout().
        await Promise.allSettled([clearChatData(), clearWorkflowData()]);
        logout();
    };

    if (!open || typeof document === "undefined") return null;
    return createPortal(
        <OverlayDialog
            open={open}
            onClose={busy ? undefined : onClose}
            dismissible={!busy}
            analyticsId={DIALOG.SIGN_OUT}
            title="Sign out of Origin?"
            width="360px"
        >
            <div className="grid gap-[14px]">
                <div className="flex items-center justify-end gap-[8px]">
                    <Button onClick={onClose} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={onSignOut} autoFocus disabled={busy}>
                        {busy ? "Signing out…" : "Sign out"}
                    </Button>
                </div>
            </div>
        </OverlayDialog>,
        document.body,
    );
}

